import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, AlertTriangle, Scale, Users, CreditCard, Ban, BookOpen, Gavel } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-network-hub/artifacts/d66f2cg2_IMG_0019.png";

export default function Terms() {
  return (
    <div className="min-h-screen bg-muted text-foreground" data-testid="terms-page">
      <header className="border-b border-border bg-card/80 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="FendanaCura" className="h-8 w-auto" />
            <span className="text-xl font-heading font-bold text-[#00A6A0]">FendanaCura</span>
          </Link>
          <Link to="/">
            <Button variant="ghost" className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-[#FF6F61]/10">
            <FileText className="w-6 h-6 text-[#FF6F61]" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-bold">Terms of Service</h1>
            <p className="text-muted-foreground text-sm">Effective Date: April 26, 2026 | Last Updated: April 26, 2026</p>
          </div>
        </div>

        {/* Compliance badges */}
        <div className="flex flex-wrap gap-2 mb-10">
          {['GDPR', 'HIPAA', 'CQC', 'PIPEDA', 'PDPA', 'SOC 2'].map(badge => (
            <span key={badge} className="px-3 py-1 rounded-full text-xs font-semibold bg-[#FF6F61]/10 text-[#FF6F61] border border-[#FF6F61]/20">{badge}</span>
          ))}
        </div>

        <div className="prose prose-invert max-w-none space-y-10">

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><Scale className="w-5 h-5" />1. Acceptance of Terms</h2>
            <p className="text-muted-foreground leading-relaxed">By accessing, downloading, installing, or using FendanaCura ("the Platform"), including our web application at fendanahealth.com, iOS application, and Android application, you ("User") agree to be bound by these Terms of Service ("Terms"). If you are registering on behalf of a care agency, healthcare organization, or other entity ("Organization"), you represent and warrant that you have the legal authority to bind that Organization to these Terms.</p>
            <p className="text-muted-foreground leading-relaxed mt-2">If you do not agree to these Terms, you must immediately cease using the Platform and delete any installed applications.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><BookOpen className="w-5 h-5" />2. Description of Service</h2>
            <p className="text-muted-foreground leading-relaxed">FendanaCura is a multi-tenant care agency management platform providing:</p>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside mt-2">
              <li>Shift scheduling, broadcasting, and bidding with AI-powered auto-matching</li>
              <li>Staff management including onboarding, certifications, compliance tracking, and disciplinary records</li>
              <li>Secure end-to-end encrypted messaging between staff, clients, and family members</li>
              <li>Care documentation including care plans, daily journals, incident reports, and medication tracking</li>
              <li>GPS-verified clock in/out, live shift tracking, and geofencing</li>
              <li>Payroll processing, invoicing, and subscription billing</li>
              <li>Analytics, dashboards, and AI-generated care summaries</li>
              <li>Emergency SOS alerts with location sharing</li>
              <li>Mobile applications with biometric authentication and offline capabilities</li>
              <li>Family portal for care visibility and communication</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">The Platform supports eight (8) user roles: CEO, Agency Administrator, Caregiver, Client, Family Member, Potential Client, Job Seeker, and Developer. Each role has distinct access permissions governed by role-based access control (RBAC).</p>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><Users className="w-5 h-5" />3. User Accounts & Registration</h2>
            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">3.1 Account Creation</h3>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>You must provide accurate, current, and complete registration information</li>
              <li>Each user receives a unique User Service Identification Code (USIC)</li>
              <li>Organizations receive a Company Identification Service Code (CISC)</li>
              <li>Staff members are assigned automatically generated staff numbers</li>
              <li>You may not create multiple accounts or share credentials</li>
            </ul>

            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">3.2 Account Security</h3>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>You are responsible for maintaining the confidentiality of your credentials</li>
              <li>You must enable multi-factor authentication (MFA) when prompted for administrative accounts</li>
              <li>You must notify us immediately of any unauthorized access at <a href="mailto:security@fendanacura.com" className="text-[#FF6F61] underline">security@fendanacura.com</a></li>
              <li>We may suspend accounts showing signs of unauthorized access or suspicious activity</li>
            </ul>

            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">3.3 Device Management</h3>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>Users may access the Platform from one registered mobile device at a time</li>
              <li>Device transfer requires administrator authorization and a secure 6-digit transfer code</li>
              <li>Lost, stolen, or damaged device replacement must follow the secure transfer process</li>
              <li>Administrators may revoke device access at any time</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><CreditCard className="w-5 h-5" />4. Subscriptions, Billing & Payments</h2>
            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">4.1 Pricing Tiers</h3>
            <div className="mt-2 p-4 bg-card border border-border rounded-xl">
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>Individual: $24.99/month (1 user)</li>
                <li>Starter: $120.99/month (1-10 users)</li>
                <li>Growth: $169.99/month (11-50 users)</li>
                <li>Professional: $200.00/month (51-100 users)</li>
                <li>Enterprise: $239.00/month (100+ users)</li>
              </ul>
            </div>

            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">4.2 Billing Terms</h3>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>Subscriptions auto-renew monthly unless cancelled before the renewal date</li>
              <li>Tier adjustments occur automatically when your active user count changes</li>
              <li>New organizations receive a 30-day free trial</li>
              <li>Payments are processed securely via Stripe or PayPal</li>
              <li>All prices are in USD unless otherwise specified</li>
              <li>Failed payments will result in a 7-day grace period before service suspension</li>
              <li>Refunds are handled on a case-by-case basis; contact <a href="mailto:billing@fendanacura.com" className="text-[#FF6F61] underline">billing@fendanacura.com</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><Ban className="w-5 h-5" />5. Acceptable Use Policy</h2>
            <p className="text-muted-foreground leading-relaxed">You agree NOT to:</p>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside mt-2">
              <li>Use the Platform for any unlawful purpose or in violation of healthcare regulations</li>
              <li>Attempt to access data belonging to other organizations (multi-tenant isolation breach)</li>
              <li>Capture, record, or screenshot sensitive client data or documents without authorization</li>
              <li>Share client documents, care records, or personal information outside the Platform except as authorized by an administrator</li>
              <li>Upload malicious content, viruses, or harmful code</li>
              <li>Reverse engineer, decompile, or disassemble any part of the Platform</li>
              <li>Misrepresent your identity, role, qualifications, or organizational affiliation</li>
              <li>Use the Platform to harass, threaten, discriminate against, or abuse other users</li>
              <li>Scrape, crawl, or otherwise harvest platform data without written authorization</li>
              <li>Circumvent security controls including screenshot prevention, access restrictions, or encryption</li>
              <li>Use automated systems (bots, scripts) to access the Platform without written permission</li>
              <li>Share login credentials or access tokens with unauthorized individuals</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-2">Violations may result in immediate account suspension, termination, and reporting to relevant regulatory authorities.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><Shield className="w-5 h-5" />6. Data Protection & Confidentiality</h2>
            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">6.1 Confidential Information</h3>
            <p className="text-muted-foreground leading-relaxed">All client data, care records, staff information, and organizational data processed through the Platform is considered Confidential Information. Users must treat all such information with the same degree of care as their own confidential information.</p>

            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">6.2 Document Security</h3>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>Client-related documents may only be downloaded by Organization Administrators</li>
              <li>Documents cannot be shared outside the Platform except by authorized administrators</li>
              <li>Screenshot and screen recording prevention is enforced on mobile applications</li>
              <li>Printing of sensitive data is restricted on the web application</li>
              <li>All document access is logged for audit purposes</li>
            </ul>

            <h3 className="text-base font-semibold text-foreground mt-3 mb-2">6.3 Healthcare Compliance</h3>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li><strong>HIPAA:</strong> Organizations using FendanaCura for US healthcare operations must execute a Business Associate Agreement (BAA)</li>
              <li><strong>CQC:</strong> The Platform supports CQC compliance requirements including care documentation, staff qualification tracking, and incident reporting</li>
              <li><strong>GDPR:</strong> Data processing is governed by our Data Processing Agreement (DPA), available upon request</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">7. Care Documentation Disclaimer</h2>
            <p className="text-muted-foreground leading-relaxed">FendanaCura provides tools for care documentation including care plans, journals, incident reports, and medication tracking. While we ensure the Platform functions reliably:</p>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside mt-2">
              <li>The accuracy and completeness of care documentation is the responsibility of the care professionals entering the information</li>
              <li>FendanaCura does not provide medical advice, diagnosis, or treatment</li>
              <li>AI-generated care summaries are assistive tools and must be reviewed by qualified professionals before acting upon</li>
              <li>The Platform does not replace professional clinical judgment</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">8. Intellectual Property</h2>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>All Platform code, design, trademarks, logos, and proprietary materials are owned by FendanaCura</li>
              <li>Your organizational data, care records, and uploaded documents remain your property</li>
              <li>You grant us a limited, non-exclusive license to process your data solely for providing the services described herein</li>
              <li>You may not reproduce, distribute, or create derivative works from the Platform without written consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">9. Service Availability & SLA</h2>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>We target 99.9% uptime for the Platform</li>
              <li>Offline mode ensures critical features remain available during connectivity disruptions</li>
              <li>Scheduled maintenance will be communicated at least 48 hours in advance</li>
              <li>Emergency maintenance may occur without prior notice to address security vulnerabilities</li>
              <li>Data is backed up daily with point-in-time recovery capability</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">10. Termination</h2>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>You may cancel your subscription at any time from your account settings</li>
              <li>You may request complete account and data deletion at <Link to="/delete-data" className="text-[#FF6F61] underline">fendanahealth.com/delete-data</Link></li>
              <li>We may suspend or terminate accounts that violate these Terms, with notice where practicable</li>
              <li>Upon termination, we will retain data only as required by law (see data retention policy in our <Link to="/privacy" className="text-[#FF6F61] underline">Privacy Policy</Link>)</li>
              <li>You may request a data export before account deletion</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><AlertTriangle className="w-5 h-5" />11. Limitation of Liability</h2>
            <p className="text-muted-foreground leading-relaxed">TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW:</p>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside mt-2">
              <li>FendanaCura is provided "AS IS" and "AS AVAILABLE" without warranties of any kind, express or implied</li>
              <li>We do not warrant that the Platform will be uninterrupted, error-free, or completely secure</li>
              <li>We shall not be liable for indirect, incidental, special, consequential, or punitive damages</li>
              <li>Our total aggregate liability shall not exceed the total amount paid by you in the twelve (12) months preceding the event giving rise to the claim</li>
              <li>Nothing in these Terms excludes liability for death, personal injury caused by negligence, fraud, or any other liability that cannot be excluded by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">12. Indemnification</h2>
            <p className="text-muted-foreground leading-relaxed">You agree to indemnify and hold harmless FendanaCura, its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses arising out of or related to your use of the Platform, violation of these Terms, or infringement of any third-party rights.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3 flex items-center gap-2"><Gavel className="w-5 h-5" />13. Governing Law & Dispute Resolution</h2>
            <ul className="text-muted-foreground space-y-1 list-disc list-inside">
              <li>These Terms shall be governed by and construed in accordance with the laws of England and Wales</li>
              <li>For EU users: You retain all consumer rights under the laws of your country of residence</li>
              <li>Disputes shall first be attempted to be resolved through good-faith negotiation</li>
              <li>If negotiation fails, disputes shall be resolved by binding arbitration or in the courts of England and Wales</li>
              <li>Nothing in these Terms limits your right to bring claims before your local consumer protection authority</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">14. Changes to Terms</h2>
            <p className="text-muted-foreground leading-relaxed">We may update these Terms from time to time. Material changes will be communicated via email and in-app notification at least 30 days before taking effect. Continued use of the Platform after changes take effect constitutes acceptance. If you do not agree to the revised Terms, you must stop using the Platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">15. Severability</h2>
            <p className="text-muted-foreground leading-relaxed">If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect.</p>
          </section>

          <section>
            <h2 className="text-xl font-heading text-[#FF6F61] mb-3">16. Contact</h2>
            <p className="text-muted-foreground leading-relaxed">For questions about these Terms:</p>
            <div className="mt-2 p-4 bg-card border border-border rounded-xl space-y-1">
              <p className="text-foreground font-medium">FendanaCura Legal</p>
              <p className="text-muted-foreground text-sm">General: <a href="mailto:legal@fendanacura.com" className="text-[#FF6F61] underline">legal@fendanacura.com</a></p>
              <p className="text-muted-foreground text-sm">Billing: <a href="mailto:billing@fendanacura.com" className="text-[#FF6F61] underline">billing@fendanacura.com</a></p>
              <p className="text-muted-foreground text-sm">Security: <a href="mailto:security@fendanacura.com" className="text-[#FF6F61] underline">security@fendanacura.com</a></p>
              <p className="text-muted-foreground text-sm">DPO: <a href="mailto:dpo@fendanacura.com" className="text-[#FF6F61] underline">dpo@fendanacura.com</a></p>
            </div>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 px-4 text-center">
        <div className="max-w-4xl mx-auto flex items-center justify-center gap-6 text-sm text-muted-foreground">
          <Link to="/privacy" className="hover:text-foreground">Privacy Policy</Link>
          <Link to="/delete-data" className="hover:text-foreground">Delete Your Data</Link>
        </div>
        <p className="text-muted-foreground text-xs mt-4">&copy; {new Date().getFullYear()} FendanaCura. All rights reserved.</p>
      </footer>
    </div>
  );
}
